import React, { useEffect, useState } from "react";
import {
  Container,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  Input,
  FormGroup,
  Alert,
} from "reactstrap";
import { toast } from 'react-toastify';
import { AlternativePagination } from "../../components/Common/Table";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { AddBanner, UpdateBanner, DeleteBanner, GetBanners, GetPromotions } from "../../requests";
import AWSS3Uploader from "../../components/Common/S3uploader";
import LoadingSpinner from "../../components/LoadingSpinner";

const Banners = () => {
  document.title = "Banners | Susipizza";

  const [modal, setModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [BannersData, setBanners] = useState([]);
  const [confirmModal, setConfirmModal] = useState(false);
  const [targetId, setTargetId] = useState(null);
  // Distinguishes Add vs Edit for the shared modal — null means "adding a
  // new banner", any id means "editing that banner".
  const [editingId, setEditingId] = useState(null);
  // Optional promotion this banner should auto-add-to-cart for when tapped
  // in the customer app (see server/promotion/PromotionService.js's
  // resolveBannerAction — only deterministic single-product Buy X Get Y
  // promotions actually auto-fill the cart; anything else falls back to
  // navigating to the menu instead).
  const [linkedPromotionId, setLinkedPromotionId] = useState("");
  const [promotions, setPromotions] = useState([]);

  const toggle = () => {
    setModal(!modal);
    if (modal) {
      // Closing — clear edit state so the next "Add Banner" click doesn't
      // reopen pre-filled with the last-edited banner's image.
      setEditingId(null);
      setImage(null);
      setLinkedPromotionId("");
    }
  };

  const handleEditClick = (row) => {
    setEditingId(row.id);
    setImage(row.imageUrl);
    setLinkedPromotionId(row.linkedPromotionId || "");
    setModal(true);
  };

  const handleSave = async () => {
    if (!image) {
      return toast.error("Please upload an image first.");
    }
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const payload = { imageUrl: image, linkedPromotionId: linkedPromotionId || null };

      if (editingId) {
        await UpdateBanner(token, editingId, payload);
        toast.success("Banner updated successfully");
      } else {
        await AddBanner(token, payload);
        toast.success("Banner added successfully");
      }
      setModal(false);
      getData();
    } catch (err) {
      toast.error(
        editingId
          ? "Error occurred while updating Banner"
          : "Error occurred while saving Banner"
      );
    } finally {
      setImage(null);
      setEditingId(null);
      setLinkedPromotionId("");
      setLoading(false);
    }
  };

  // Same eligibility rule as the server's resolveBannerAction — shown here
  // only as a hint to the admin, not enforced; the server is the real gate
  // at tap time.
  const isAutoAddEligible = (promo) => {
    const qualificationType = String(promo?.qualification?.type || "").toUpperCase();
    const rewardType = String(promo?.reward?.type || "").toUpperCase().replace(/[^A-Z]/g, "");
    const rewardPolicy = String(promo?.policy?.rewardPolicy || "").toUpperCase().replace(/[^A-Z]/g, "");
    const buyItems = Array.isArray(promo?.qualification?.buyItems) ? promo.qualification.buyItems : [];
    const rewardItems = Array.isArray(promo?.reward?.rewardItems) ? promo.reward.rewardItems : [];
    return (
      qualificationType === "BUYPRODUCT" &&
      buyItems.length === 1 &&
      rewardType === "FREEPRODUCT" &&
      rewardItems.length === 1 &&
      ["FIXEDPRODUCT", "SPECIFICPRODUCT"].includes(rewardPolicy)
    );
  };

  const fetchPromotions = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await GetPromotions(token);
      const active = (response.data || []).filter(
        (p) => p.status === "active" || p.status === "enable"
      );
      setPromotions(active);
    } catch (err) {
      console.log(err);
    }
  };

  const handleDeleteClick = (id) => {
    setTargetId(id);
    setConfirmModal(true);
  };

  const confirmDelete = async () => {
    setConfirmModal(false);
    if (!targetId) return;
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      await DeleteBanner(token, targetId);
      toast.success("Banner removed successfully");
      getData();
    } catch (err) {
      toast.error("Error occurred while removing banner");
    } finally {
      setLoading(false);
      setTargetId(null);
    }
  };

  const getData = async () => {
    try {
      setLoading(true);
      const resp = await GetBanners();

      setBanners(resp.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
    fetchPromotions();
  }, []);

  const columns = [
    {
      name: <span className="font-weight-bold fs-13">#</span>,
      selector: (row, index) => (index + 1).toString(),
      // sortable: true
    },
    {
      name: <span className="font-weight-bold fs-13">Image</span>,
      // Fixed column width matching the thumbnail's own size — without this
      // the table splits width evenly across columns by default while the
      // <img> below stays hard-coded to 300px, so it overflows its cell and
      // visually overlaps whatever column comes after it (Tap Action).
      width: "330px",
      cell: (row) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <img
            src={row.imageUrl}
            alt="image"
            style={{ width: "300px", height: "125px" }}
          />
        </div>
      ),
    },
    {
      name: <span className="font-weight-bold fs-13">Tap Action</span>,
      width: "180px",
      cell: (row) => {
        const promo = promotions.find((p) => p.id === row.linkedPromotionId);
        return row.linkedPromotionId ? (
          <span className="badge bg-success">{promo ? promo.code : "Linked offer"}</span>
        ) : (
          <span className="text-muted">None</span>
        );
      },
    },
    {
      name: <span className="font-weight-bold fs-13">Action</span>,
      cell: (row) => {
        return (
          <div style={{ display: "flex", gap: "8px" }}>
            <button className="btn btn-outline-primary" onClick={() => handleEditClick(row)}>Edit</button>
            <button className="btn btn-outline-danger" onClick={() => handleDeleteClick(row.id)}>Remove</button>
          </div>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <LoadingSpinner loading={loading} />
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="susipizza" breadcrumbItem="Banners" />
          <button
            type="button"
            class="btn btn-primary mb-3"
            onClick={() => {
              setModal(true);

            }}
          >
            Add Banner
          </button>
          <AlternativePagination columns={columns} data={BannersData} />
        </Container>
      </div>

      {/* Modal */}

      <Modal isOpen={modal} toggle={toggle} centered>
        <ModalHeader toggle={toggle}>{editingId ? "Edit Banner" : "Add Banner"}</ModalHeader>
        <ModalBody className="modal-color">
          {editingId && (
            <FormGroup>
              <Label>Current Image</Label>
              <div>
                <img
                  src={image}
                  alt="current banner"
                  style={{ width: "200px", height: "auto", display: "block", marginBottom: "8px", borderRadius: "4px" }}
                />
              </div>
            </FormGroup>
          )}
          <FormGroup>
            <Label>{editingId ? "Replace Image (optional)" : "Image"}</Label>
            <AWSS3Uploader onUploadComplete={(url) => setImage(url)} folder="banners" />
          </FormGroup>
          <FormGroup>
            <Label>Tap Action — Auto-add offer to cart (optional)</Label>
            <Input
              type="select"
              value={linkedPromotionId}
              onChange={(e) => setLinkedPromotionId(e.target.value)}
            >
              <option value="">No action (image only)</option>
              {promotions.map((promo) => (
                <option key={promo.id} value={promo.id}>
                  {promo.name} ({promo.code}){!isAutoAddEligible(promo) ? " — will open menu, not auto-add" : ""}
                </option>
              ))}
            </Input>
            <small className="text-muted">
              When linked, tapping this banner in the customer app adds the offer's
              qualifying item straight to the cart and applies the coupon. Only
              simple "Buy 1 Get 1"-style, single-product offers can auto-add; other
              offer types will instead take the customer to the menu.
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSave}>
            Save
          </Button>{" "}
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={confirmModal} toggle={() => setConfirmModal(!confirmModal)} centered>
        <ModalHeader toggle={() => setConfirmModal(!confirmModal)}>Confirm Deletion</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this banner? This action cannot be undone.
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={confirmDelete}>Delete</Button>{' '}
          <Button color="secondary" onClick={() => setConfirmModal(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Banners;
